import { ApolloClient } from '@apollo/client';
import { SchedulingFormType } from '../reportViewer/ReportViewer.types';

export class OptionType {
  value: string;
  displayName: string;

  constructor(value: string, displayName: string) {
    this.value = value;
    this.displayName = displayName;
  }
}
export interface DateRange {
  startDay: Date | string;
  endDay: Date | string;
}
export interface ParameterFormData {
  name: string;
  description: string;
  dataTypeId: number;
  query?: string;
  userRoleIds: number[];
  queryFieldName: string;
  id: number;
  versionId: number;
}

export interface Config {
  VERSION: string;
  API_URL: string;
  RETRY_TIME: number;
  RETRY_DELAY: number;
  TOAST_AUTOCLOSE_INTERVAL: number;
  HEAP_API_URL: string;
  HEAP_API_KEY: string;
  OIDC_CONFIGURATION: OidcConfiguration;
  RAAS_URL: string;
  RAAS_URL_ROLES: string[];
  ADMIN_ROLES: string[];
  INACTIVITY_CONFIG: InactivityConfig;
  HR_ROLES: string[];
  COLUMNS_NUMBER_OF_PERFORMANCE_WARNING: number;
  PRESET_DATES: PresetDate[];
  BUSINESS_USER_ROLES: string[];
}

export interface PresetDate {
  value: string;
}
interface InactivityConfig {
  inactive_timeout: number;
  logout_timeout: number;
}

export interface ChangeableTypeProps {
  role?: string;
  className?: string;
  isUserType?: boolean;
  fieldName?: string;
  error?: string;
  loading?: boolean;
  formValue?: string;
}
export interface CreateLogRequest {
  action: AuditLogAction;
  message?: string;
  detail?: string;
}

export interface OidcConfiguration {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope: string;
  authority: string;
  silent_redirect_uri: string;
  automaticSilentRenew: boolean;
  loadUserInfo: boolean;
}

export enum DataType {
  ReportDefinitionColumn = 0,
  Parameter = 1,
}

export interface RunReportParamsType {
  columns: string[];
  parameters: { [key: number]: string[] };
  reportDefinitionId: number;
}

export interface RunReportDataType {
  reportName: string;
  runReportParams: RunReportParamsType;
  correlationId: string;
  exportFileCorrelationId: string;
}

export enum InactivePopupAction {
  LOGOUT = 'logout',
  KEEPLOGIN = 'keepLogin',
}

export type FormatPreviewRowType = { [key: string]: string };
export type PreviewRowType = { [key: string]: any };
export interface DownloadSavedReport {
  savedReportId: number;
  client: ApolloClient<any>;
  onNoData?: () => void;
  onSuccess: () => void;
}

export interface DownloadRecentlyGeneratedReport {
  itemId: number;
  client: ApolloClient<any>;
  onNoData?: () => void;
  onSuccess: () => void;
}

export interface DownloadReportByParams {
  client: ApolloClient<any>;
  onNoData?: () => void;
  onSuccess: () => void;
  bodyParams: RunReportParamsType;
}

export interface CheckExportDataAmountAndDownloadFuncParams {
  client: ApolloClient<any>;
  onNoData?: () => void;
  onSuccess: () => void;
  correlationId: string;
}

export interface RunReportDataFuncParams {
  offset: number;
  limit: number;
  runReportParams: RunReportParamsType;
  client: ApolloClient<any>;
}

export interface RunSavedReportDataFuncParams {
  offset: number;
  limit: number;
  client: ApolloClient<any>;
  savedReportId: number;
}

export interface FetchReportDataFuncParams {
  correlationId: string;
  page: number;
  client: ApolloClient<any>;
  shouldPolling?: boolean;
}

export interface RunSavedReportParams {
  offset: number;
  limit: number;
  client: ApolloClient<any>;
  savedReportId: number;
}

export interface RoleSwitchType {
  label: string;
  value: string;
  path: string;
}

export enum SimpleCancelButtonTheme {
  BLUE = 'blue',
  WHITE = 'white',
}

export interface RunReportJobStatusFuncParams {
  client: ApolloClient<any>;
  correlationId: string;
}

export interface GetRunReportTotalRowsFuncParams {
  client: ApolloClient<any>;
  correlationId: string;
}

export interface CheckExportJobStatusFuncParams {
  client: ApolloClient<any>;
  correlationId: string;
}

export interface FetchAndFormatNewReportDataFuncParams {
  page: number;
  dataOffset: number;
  requestCorrelationId: string;
  shouldPolling?: boolean;
  shouldUpdatePage?: boolean;
}

export interface FetchSnapshotDataFuncParams {
  snapshotId: number;
  client: ApolloClient<any>;
}

export interface FetchSnapshotDataFuncPayload {
  data: string;
  hasMoreData: boolean;
  reportDefinitionName: string;
  maximumRows: number;
}

export interface FetchSnapshotJobStatusFuncParams {
  snapshotCorrelationId: string;
  fetchingSnapshotId: number;
  client: ApolloClient<any>;
}

export const ScheduleType: Record<string, string> = {
  Weekly: 'weekly',
  Monthly: 'monthly',
};

export interface LogActionType {
  apiURL: string;
}
export interface LogErrorType extends LogActionType {
  errorMessage: string;
  errorStack?: string;
}

export enum AuditLogAction {
  LogError = 'LogError',
  Login = 'Login',
  Logout = 'Logout',
  EndUserClickOnBIDashboard = 'EndUserClickOnBIDashboard',
  EndUserRunSnapshotReportAgain = 'EndUserRunSnapshotReportAgain',
}
export interface ManuallyLoggedErrorType {
  functionName: string;
}
export interface DownloadDataPayload {
  fileUrl: StringOrEmpty;
  fileName: StringOrEmpty;
}

export interface DownloadReportFileFuncParams {
  onSuccess: () => void;
  fileUrl: string;
  fileName: StringOrEmpty;
}

export type StringOrEmpty = string | undefined | null;
export enum ReportSnapshotType {
  Adhoc = 'Adhoc',
  Saved = 'Saved',
  Scheduled = 'Scheduled',
  Shared = 'Shared',
}

export type UserEditReportParams = {
  reportId: string;
};

export interface GetDefaultScheduledReportValueFuncParams {
  selectedTab: string;
  weeklyTabName: string;
  monthlyTabName: string;
  defaultFormValues: SchedulingFormType | undefined;
}

export interface ToggleSchedulingFuncParams {
  checked: boolean;
  defaultFormValues: SchedulingFormType | undefined;
  weeklyTabName: string;
  monthlyTabName: string;
  setSelectedTab: (tab: string) => void;
}

export enum WeekDay {
  sunday = 'sunday',
  monday = 'monday',
  tuesday = 'tuesday',
  wednesday = 'wednesday',
  thursday = 'thursday',
  friday = 'friday',
  saturday = 'saturday',
}

export interface UserInfo {
  expire_at: number;
  access_token: string;
  profile: {
    auth_time: number;
    FullName: string;
    TenantId: string;
    UserID: number;
    ExtRole: string;
    Roles: string;
  };
}

export interface FetchReportDataFuncResponse {
  reportData: any[];
  isCorrelationIdExpired: boolean;
}
